import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { FC, useEffect, useMemo, useState } from "react";

import { Uuid } from "@/domains/global/identifiers";
import type { AudioRecordingObservable } from "@/store/audio-recordings";
import { useAppStore } from "@/store";
import {
  AudioRecordingProcessingStatus,
  AudioRecordingRecordingStatus,
  AudioRecordingUploadStatus,
} from "@/store/audio-recordings";
import {
  AudioRecordingSyncStatusCard,
  CapturedAudioArtifactCard,
  CapturedAudioUtilitiesCard,
} from "@/pages/audio-recordings/components";
import { useLocalAudioArtifactDebugData } from "@/pages/audio-recordings/shared";
import { MDSButton, MDSButtonVariant, MDSText, MDSTextColor, MDSTextVariant } from "@/mds";
import { AudioCapturePhase } from "@/store/audio/pipeline/audio-capture.types";
import { projectCapturePipelineUiState } from "@/store/audio/pipeline/audio-pipeline.ui-projection";
import {
  Stage,
  TerminalOutcome,
  type PipelineSnapshot,
} from "@/store/audio/pipeline/audio-pipeline.types";
import {
  AudioSourceUploadStateValue,
  getSourceUploadState,
} from "@/store/audio/pipeline/source-upload-state";

import { InfoHeader } from "./sections/info-header";
import { InfoTranscriptionSection } from "./sections/info-transcription-section";
import { InfoStatusMessage } from "./sections/info-status-message";

type Props = {
  recordingId: Uuid;
  recording?: AudioRecordingObservable;
};

const TRASH_CONFIRM_MESSAGE = "Trash local audio and transcript data for this recording?";

export const AudioRecordingDetailInfo: FC<Props> = observer(({ recordingId, recording }) => {
  const { store } = useAppStore();
  const audioStore = store.audio;
  const [actionError, setActionError] = useState<string | null>(null);
  const recordingObservable = recording ?? store.audioRecordings.get(recordingId);
  const {
    data: localArtifactData,
    error: localArtifactError,
    isLoading,
    isRefreshing,
    refresh,
  } = useLocalAudioArtifactDebugData({
    audioRecordingId: recordingId,
    recordingStrategy: recordingObservable?.recordingStrategy ?? null,
  });

  const headerStats = useMemo(() => {
    if (!localArtifactData) {
      return null;
    }

    return {
      artifactStateLabel: localArtifactData.artifactStateLabel,
      totalDurationLabel: localArtifactData.totalDurationLabel,
      totalSizeLabel: localArtifactData.totalSizeLabel,
      unitCount: localArtifactData.unitCount,
      unitLabel: localArtifactData.unitLabel,
    };
  }, [localArtifactData]);

  useEffect(() => {
    void store.audioRecordings.getAsync(recordingId);
  }, [recordingId, store.audioRecordings]);

  const snapshot = audioStore.pipeline.getSnapshot(recordingId);
  const isActiveRecording = audioStore.activeRecordingId === recordingId;
  const pipelineUi = projectCapturePipelineUiState({
    snapshot,
    recording: recordingObservable ?? null,
    isInitializing: isActiveRecording && audioStore.recording.isInitializing,
    isRecordingPaused: isActiveRecording && audioStore.recording.isPaused,
  });
  const recordingStatus = (recordingObservable?.recordingStatus ??
    AudioRecordingRecordingStatus.INITIAL) as AudioRecordingRecordingStatus;
  const uploadStatus = (recordingObservable?.uploadStatus ??
    AudioRecordingUploadStatus.INITIAL) as AudioRecordingUploadStatus;
  const processingStatus = (recordingObservable?.processingStatus ??
    AudioRecordingProcessingStatus.INITIAL) as AudioRecordingProcessingStatus;
  const sourceUploadState = getSourceUploadState({ store, audioRecordingId: recordingId });
  const hasLocalArtifact = (localArtifactData?.totalSizeBytes ?? 0) > 0;
  const terminalOutcome = snapshot?.stage === Stage.Done ? snapshot.terminalOutcome : null;
  const hasCompletedProcessing =
    terminalOutcome === TerminalOutcome.Success ||
    processingStatus === AudioRecordingProcessingStatus.COMPLETED;
  const hasTerminalPipelineBlocker =
    terminalOutcome === TerminalOutcome.NoUsableAudioFound ||
    terminalOutcome === TerminalOutcome.AudioArtifactMissing ||
    uploadStatus === AudioRecordingUploadStatus.SKIPPED;
  const hasAnyProcessableArtifact =
    hasLocalArtifact || sourceUploadState.state === AudioSourceUploadStateValue.Completed;
  const canStartSourceUpload =
    hasLocalArtifact &&
    recordingStatus === AudioRecordingRecordingStatus.COMPLETED &&
    sourceUploadState.state !== AudioSourceUploadStateValue.Uploading &&
    sourceUploadState.state !== AudioSourceUploadStateValue.Completed;
  const canRunPipeline =
    recordingStatus === AudioRecordingRecordingStatus.COMPLETED &&
    !pipelineUi.isProcessing &&
    !hasCompletedProcessing &&
    !hasTerminalPipelineBlocker &&
    hasAnyProcessableArtifact;
  const canPlayAudio =
    recordingStatus === AudioRecordingRecordingStatus.COMPLETED && hasLocalArtifact;

  const handleStartSourceUpload = () => {
    if (!canStartSourceUpload) return;
    setActionError(null);
    void audioStore.startSourceUpload({
      audioRecordingId: recordingId,
      forceRestart: sourceUploadState.state === AudioSourceUploadStateValue.Error,
    });
  };

  const handleRunPipeline = () => {
    if (!canRunPipeline) return;
    setActionError(null);
    void audioStore.processRecording({ audioRecordingId: recordingId });
  };

  const handleRefresh = () => {
    if (isRefreshing) return;
    setActionError(null);
    void refresh({ showSpinner: false });
  };

  const handleTrash = async () => {
    const confirmed = window.confirm(TRASH_CONFIRM_MESSAGE);
    if (!confirmed) return;

    setActionError(null);
    try {
      await audioStore.trashRecording({ audioRecordingId: recordingId });
      await refresh();
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to clear local data.";
      setActionError(message);
    }
  };

  const handleViewActiveState = () => {
    store.navigation.goToAudioRecordingActiveState({
      openInSameTab: true,
      audioRecordingId: recordingId,
    });
  };

  const showContent = !isLoading && !localArtifactError && !actionError;

  return (
    <PageStack>
      <Card>
        <InfoHeader isLoading={isLoading} stats={headerStats} />

        <DurationRow>
          <MDSText variant={MDSTextVariant.HeaderS}>
            {headerStats?.totalDurationLabel ?? "00:00"}
          </MDSText>
          <MDSText variant={MDSTextVariant.TextSm} color={MDSTextColor.Secondary}>
            total captured duration
          </MDSText>
        </DurationRow>

        <ActionRow>
          <MDSButton variant={MDSButtonVariant.Emphasis} onClick={handleViewActiveState}>
            Open active state page
          </MDSButton>
          <MDSButton
            variant={MDSButtonVariant.Transparent}
            onClick={handleRefresh}
            isDisabled={isRefreshing}
          >
            {isRefreshing ? "Refreshing…" : "Refresh local data"}
          </MDSButton>
        </ActionRow>

        {actionError && <InfoStatusMessage tone="error">{actionError}</InfoStatusMessage>}
        {!actionError && localArtifactError && (
          <InfoStatusMessage tone="error">{localArtifactError}</InfoStatusMessage>
        )}
        {isLoading && !localArtifactError && !actionError && (
          <InfoStatusMessage>Loading local artifact data…</InfoStatusMessage>
        )}
      </Card>

      <AudioRecordingSyncStatusCard recording={recordingObservable} />

      <Card>
        <InfoRow>
          <MDSText variant={MDSTextVariant.TextSm} color={MDSTextColor.Secondary}>
            Audio recording ID
          </MDSText>
          <MDSText variant={MDSTextVariant.TextSm}>{recordingId}</MDSText>
        </InfoRow>
      </Card>

      {showContent && (
        <SectionsStack>
          <Card>
            <InfoTranscriptionSection recording={recordingObservable} />
          </Card>
          <CapturedAudioArtifactCard data={localArtifactData} />
        </SectionsStack>
      )}

      <CapturedAudioUtilitiesCard
        audioRecordingId={recordingId}
        canPlay={canPlayAudio}
        onStartSourceUpload={handleStartSourceUpload}
        onRunPipeline={handleRunPipeline}
        canStartSourceUpload={canStartSourceUpload}
        canRunPipeline={canRunPipeline}
        sourceUploadStatus={formatSourceUploadStatusLabel(sourceUploadState)}
        pipelineStatus={formatPipelineStatusLabel({
          snapshot,
          pipelineUi,
          recordingStatus,
          processingStatus,
        })}
        pipelineOutcome={formatPipelineOutcomeLabel(terminalOutcome)}
        pipelineFailureMessage={pipelineUi.failureMessage}
        sourceUploadButtonLabel={getSourceUploadButtonLabel(sourceUploadState.state)}
        runPipelineButtonLabel={getPipelineButtonLabel({
          terminalOutcome,
          processingStatus,
        })}
        onDeleteSession={handleTrash}
        deleteButtonLabel="Clear Local Data"
      />
    </PageStack>
  );
});

const PageStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding-top: 1rem;
  padding-left: 1.5rem;
  padding-right: 1.5rem;
  padding-bottom: 1.5rem;
`;

const SectionsStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Card = styled.section`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem;
  border: 1px solid ${({ theme }) => theme.colors.border.surface};
  border-radius: 0.75rem;
  background-color: ${({ theme }) => theme.colors.surface.pane};
`;

const InfoRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const DurationRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
`;

const ActionRow = styled.div`
  display: flex;
  justify-content: flex-start;
  gap: 0.75rem;
`;

function formatSourceUploadStatusLabel({
  state,
  progressPercent,
}: {
  state: AudioSourceUploadStateValue;
  progressPercent: number | null;
}): string {
  switch (state) {
    case AudioSourceUploadStateValue.Uploading: {
      const percent =
        typeof progressPercent === "number"
          ? Math.max(0, Math.min(100, Math.round(progressPercent)))
          : null;
      return percent === null ? "Uploading" : `Uploading (${percent}%)`;
    }
    case AudioSourceUploadStateValue.Error:
      return "Failed";
    case AudioSourceUploadStateValue.Skipped:
      return "Skipped";
    case AudioSourceUploadStateValue.Completed:
      return "Completed";
    case AudioSourceUploadStateValue.Idle:
      return "Not started";
  }
}

function getSourceUploadButtonLabel(state: AudioSourceUploadStateValue): string {
  return state === AudioSourceUploadStateValue.Error
    ? "Retry source upload"
    : "Start source upload";
}

function getPipelineButtonLabel({
  terminalOutcome,
  processingStatus,
}: {
  terminalOutcome: TerminalOutcome | null;
  processingStatus: AudioRecordingProcessingStatus;
}): string {
  if (
    (terminalOutcome !== null && terminalOutcome !== TerminalOutcome.Success) ||
    processingStatus === AudioRecordingProcessingStatus.ERROR
  ) {
    return "Retry pipeline";
  }

  return "Run pipeline";
}

function formatPipelineStatusLabel({
  snapshot,
  pipelineUi,
  recordingStatus,
  processingStatus,
}: {
  snapshot: PipelineSnapshot | null;
  pipelineUi: ReturnType<typeof projectCapturePipelineUiState>;
  recordingStatus: AudioRecordingRecordingStatus;
  processingStatus: AudioRecordingProcessingStatus;
}): string {
  if (snapshot) {
    if (snapshot.stage === Stage.Done) {
      return snapshot.terminalOutcome === TerminalOutcome.Success ? "Completed" : "Failed";
    }

    return `Running: ${describeStage(snapshot.stage)}`;
  }

  if (pipelineUi.phase === AudioCapturePhase.Uploading) {
    return pipelineUi.processingLabel;
  }

  if (pipelineUi.phase === AudioCapturePhase.Processing) {
    return "Running";
  }

  if (processingStatus === AudioRecordingProcessingStatus.COMPLETED) {
    return "Completed";
  }

  if (processingStatus === AudioRecordingProcessingStatus.ERROR) {
    return "Failed";
  }

  if (recordingStatus === AudioRecordingRecordingStatus.IN_PROGRESS) {
    return "Recording in progress";
  }

  if (recordingStatus === AudioRecordingRecordingStatus.COMPLETED) {
    return "Idle";
  }

  return "Not started";
}

function formatPipelineOutcomeLabel(outcome: TerminalOutcome | null): string | null {
  if (outcome === null || outcome === TerminalOutcome.Success) {
    return null;
  }

  switch (outcome) {
    case TerminalOutcome.NoUsableAudioFound:
      return "No usable audio found";
    case TerminalOutcome.AudioArtifactMissing:
      return "Audio artifact missing";
    case TerminalOutcome.UploadFailed:
      return "Source upload failed";
    case TerminalOutcome.RetranscribeFailed:
      return "Retranscribe failed";
    case TerminalOutcome.TransformFailed:
      return "Transform failed";
    case TerminalOutcome.UnexpectedFailed:
      return "Unexpected failure";
  }
}

function describeStage(stage: Stage): string {
  switch (stage) {
    case Stage.Recording:
      return "Recording";
    case Stage.Finalize:
      return "Finalize";
    case Stage.ValidateAudio:
      return "Validate audio";
    case Stage.ResolveTranscriptSource:
      return "Resolve transcript source";
    case Stage.UploadSource:
      return "Upload source";
    case Stage.RetranscribeFromSource:
      return "Retranscribe from source";
    case Stage.Transform:
      return "Transform";
    case Stage.Done:
      return "Done";
  }
}
